import axios from 'axios'
import { getShareToken } from '../../helpers/shareToken'

if (window.location.pathname === '/login') {
  const token = localStorage.getItem('token')
  if (token !== null) {
    localStorage.clear()
  }
}

const defaultConfig = {
}

const getConfig = (auth) => {
  if (!auth) {
    return defaultConfig
  }

  return {
    ...defaultConfig,
    headers: {
      ...defaultConfig.headers,
      Authorization: localStorage.getItem('token'),
    },
  }
}

const formatErrorMessage = (status, data) => {
  return data && data.length > 0 && data[0].message || `Error ${status}`
}

const onSuccess = res => {
  return {
    data: (res.status === 200 || res.status === 302) ? res.data : formatErrorMessage(res.status, res.response?.data), headers: res.headers, status: res.status
  }
}
const onError = err => ({ data: null, headers: err.headers, error: { status: err.status } })

axios.interceptors.response.use(response => response, error => {
  if (error.status === 401) {
    if (error.config?.url === '/api/auth/sign-out') return Promise.reject(error)
    localStorage.clear()
    if (window.location.pathname !== '/login') {
      const retpath = `${window.location.pathname}${window.location.search}`
      window.location.href = `/login?retpath=${encodeURIComponent(retpath)}`
    }
  } else {
    return error
  }
})

const appendShareTokenToUrl = (url, shareToken) => {
  if (!shareToken) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}share=${encodeURIComponent(shareToken)}`
}

// A share link must not override the signed-in user's permissions. Try the
// regular authorization first and only fall back to the share token when
// that resource is forbidden for the current user.
const withShareTokenFallback = (request, shareToken, hasAuthorizedAccess) => (
  request(!!shareToken && !hasAuthorizedAccess).then(response => {
    if (response.status !== 403 || !shareToken || !hasAuthorizedAccess) {
      return response
    }
    return request(true)
  })
)

// The share token is always the one from the current URL (see getShareToken) —
// `params` must not carry its own `share` key, so there is only one source of truth.
export const get = (url, auth, /** @type { import('axios').AxiosRequestConfig } */ params) => {
  const requestParams = !params ? {} : { ...params }
  const shareToken = getShareToken()
  const hasAuthorizedAccess = auth && !!localStorage.getItem('token')

  const request = (useShareToken) => {
    const finalParams = { ...requestParams }
    if (useShareToken && shareToken) {
      finalParams.share = shareToken
    }

    return axios.get(url, {
      ...getConfig(auth && !useShareToken),
      params: finalParams,
    }).then(onSuccess).catch(onError).then(response => ({
      ...response,
      shareAccess: useShareToken,
    }))
  }

  return withShareTokenFallback(request, shareToken, hasAuthorizedAccess)
}

export const post = (url, data, auth) =>
  axios.post(url, data, getConfig(auth)).then(onSuccess).catch(onError)

export const postForm = (url, data, auth) =>
  axios.post(url, data, getConfig(auth)).then(onSuccess).catch(onError)

export const put = (url, data, auth) =>
  axios.put(url, data, getConfig(auth)).then(onSuccess).catch(onError)

export const patch = (url, data, auth) =>
  axios.patch(url, data, getConfig(auth)).then(onSuccess).catch(onError)

export const putJSON = (url, data, auth) => {
  const config = getConfig(auth)
  config.headers['Content-Type'] = 'application/json'
  return axios.put(url, data, config).then(onSuccess).catch(onError)
}

export const remove = (url, auth) =>
  axios.delete(url, getConfig(auth)).then(onSuccess).catch(onError)

export const del = (url, auth) => { return axios.delete(url, getConfig(auth)) }

export const file = (url) => {
  const shareToken = getShareToken()
  const hasAuthorizedAccess = !!localStorage.getItem('token')

  const request = (useShareToken) => axios.get(
    useShareToken ? appendShareTokenToUrl(url, shareToken) : url,
    {
      headers: {
        'Accept': 'application/octet-stream, */*',
        ...(useShareToken ? {} : { 'Authorization': localStorage.getItem('token') }),
      },
      responseType: 'blob',
    }
  )

  return withShareTokenFallback(request, shareToken, hasAuthorizedAccess)
}
